'use client';

import { Check } from '@phosphor-icons/react';
import { useLocale } from 'next-intl';
import { useStockDisplay } from '@/lib/storefront/stock-display';

interface StockBadgeProps {
  stock: number | null;
  trackStock?: boolean;
}

// Soft pill: light tinted background + tonal text + matching border.
// `w-fit` is critical — without it the badge becomes a flex item in column
// layouts (PDP, cards) and stretches to full width.
const PILL =
  'inline-flex w-fit items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold border';

export default function StockBadge({ stock, trackStock = true }: StockBadgeProps) {
  const locale = useLocale();
  const display = useStockDisplay();
  const isAr = locale === 'ar';

  // Store has opted to hide stock indicators entirely.
  if (display === 'hide') return null;
  if (!trackStock || stock === null || stock === undefined) return null;

  // Out of stock.
  if (stock <= 0) {
    return (
      <span className={`${PILL} bg-red-50 text-red-700 border-red-200`}>
        {isAr ? 'نفد المخزون' : 'Out of stock'}
      </span>
    );
  }

  // Low stock — surface the remaining count as a warning.
  if (stock <= 3) {
    return (
      <span className={`${PILL} bg-amber-50 text-amber-700 border-amber-200`}>
        {isAr ? `باقي ${stock} فقط` : `Only ${stock} left`}
      </span>
    );
  }

  // In stock — soft green pill with a check and the available count.
  return (
    <span className={`${PILL} bg-emerald-50 text-emerald-700 border-emerald-200`}>
      <Check size={12} weight="bold" />
      {isAr ? `${stock} متوفر` : `${stock} in stock`}
    </span>
  );
}
